import {
  Tab as MuiTab,
  Tabs as MuiTabs,
  TabProps as MuiTabProps,
  TabsProps as MuiTabsProps,
} from '@mui/material'
import { ReactNode } from 'react'
import styled, { css } from 'styled-components'

interface TabProps extends MuiTabProps {
  isReactNative?: boolean
}

interface TabsProps extends MuiTabsProps {
  isReactNative?: boolean
}

export const TabsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: ${({ theme }) => theme.space.sm}px 0
    ${({ theme }) => theme.space.sm}px;
  box-shadow: inset 0px -1px 0px ${({ theme }) => theme.colors.Grey};
`

export const Tabs = styled(MuiTabs)<TabsProps>`
  box-shadow: inset 0px -1px 0px ${({ theme }) => theme.colors.Grey};
  display: ${({ isReactNative }) => (isReactNative ? 'flex' : 'auto')};
`

export const Tab = styled(MuiTab)<TabProps>`
  display: flex; // Set display to flex
  justify-content: center; // Center content horizontally
  align-self: center; // Center content vertically
  align-items: center; // Center content vertically
  font-family: Poppins;
  font-weight: 600;
  font-size: ${({ isReactNative }) => (isReactNative ? '14px' : '16px')};
  line-height: 24px;
  letter-spacing: -0.004em;
  color: ${({ theme }) => theme.colors.Grey60};
  text-transform: none;
  padding-left: 0;
  padding-right: 0;
  margin-right: ${({ isReactNative }) => (isReactNative ? 'auto' : '40px')};
  align-items: flex-start;
  min-width: unset;
  flex-grow: ${({ isReactNative }) => (isReactNative ? '1' : 'auto')};
  > * {
    font-weight: 600;
  }

  &.Mui-selected,
  &:hover {
    color: ${({ theme }) => theme.colors.MidnightBlue};
  }
`

export const MobileTab = styled(MuiTab)`
  font-family: Poppins;
  letter-spacing: -0.004em;
  color: ${({ theme }) => theme.colors.Grey60};
  text-transform: none;
  padding-left: ${({ theme }) => theme.space.ms}px;
  padding-right: ${({ theme }) => theme.space.ms}px;
  margin-right: ${({ theme }) => theme.space.ms}px;
  align-items: flex-start;
  min-width: unset;
  > * {
    font-weight: 600;
  }

  &.Mui-selected,
  &:hover {
    color: ${({ theme }) => theme.colors.MidnightBlue};
  }
`

// Transient ($-prefixed) props so styled-components doesn't forward them to the
// underlying DOM node.
export const TabPanelContainer = styled.div<{
  $overflowX?: boolean
  $fullBleed?: boolean
}>`
  width: 100%;
  /* Use clip instead of hidden for x-axis to allow position: sticky in children.
     clip doesn't create a scroll container like hidden does. */
  overflow-x: ${({ $overflowX }) => ($overflowX ? 'auto' : 'clip')};
  /* Break out of the page content padding so the panel spans the full tab
     width. The breakout has to live on this (the clip) element: a negative
     margin on a descendant would be clipped back by overflow-x. Driven by the
     --page-content-padding variable set on MainLayout's Content, so it always
     matches the real padding (falls back to a no-op when unset). */
  ${({ $fullBleed }) =>
    $fullBleed &&
    css`
      width: calc(100% + 2 * var(--page-content-padding, 0px));
      margin-left: calc(-1 * var(--page-content-padding, 0px));
      margin-right: calc(-1 * var(--page-content-padding, 0px));
    `}
`

interface TabPanelProps {
  children?: ReactNode
  value: string | number
  index: string | number
  overflowX?: boolean
  /* Span the full tab width, ignoring MainLayout's page content padding.
     Children that should stay inset must re-add var(--page-content-padding). */
  fullBleed?: boolean
}

export function TabPanel({
  children,
  value,
  index,
  overflowX,
  fullBleed,
}: TabPanelProps) {
  return (
    <TabPanelContainer $overflowX={overflowX} $fullBleed={fullBleed}>
      {value === index && <span>{children}</span>}
    </TabPanelContainer>
  )
}
